import { useCallback, useEffect, useState } from 'react';

import api from '../../../services/api';
import { isOk } from '../../../utils/apiResult';

// ─────────────────────────────────────────────────────────────────────────
// Microsoft 365 takvim senkronu — ayar ekranının API katmanı.
//
// Sözleşme backend DTO dosyasından BİZZAT okunarak yazıldı
// (backend/Flexbee.Api/Dtos/MicrosoftCalendarDtos.cs):
//   GET    /v1/integrations/microsoft-calendar
//          → MicrosoftCalendarStatusResponse
//            { connected, azureTenantId, clientId, isEnabled,
//              lastErrorClass, failedCount }
//   PUT    /v1/integrations/microsoft-calendar   { azureTenantId, clientId, clientSecret }
//          → aynı MicrosoftCalendarStatusResponse (ikinci bir GET GEREKMEZ)
//   DELETE /v1/integrations/microsoft-calendar
//   POST   /v1/integrations/microsoft-calendar/test
//          → MicrosoftCalendarTestResult { status, errorClass, error }
//
// GİZLİ ANAHTAR HİÇBİR YANITTA YOKTUR — kısmi gösterim bile yok. Ekran yalnız
// "bağlı / bağlı değil" bilir; alan boş gelirse "değiştirmek için yeniden gir".
// ─────────────────────────────────────────────────────────────────────────

// "BAĞLI DEĞİL" ≠ "YÜKLENEMEDİ".
//
// services/api.js interceptor'ı GET hatalarını ASLA reddetmez: 500 de, ağ
// hatası da `{data:{status:false,data:null}}` ile RESOLVE olur (bkz.
// utils/apiResult.js sınıf yorumu). `.catch` bu yüzden ÖLÜ KODDUR ve
// `res.data?.data || {connected:false}` yazan bir hook, ULAŞILAMAYAN sunucuyu
// "bağlı değil" diye gösterirdi — İK da var olan bir bağlantıyı yeniden
// kurmaya çalışırdı. Gerçek sinyal `isOk(res)`'tir ve AYRI bir `loadFailed`
// durumuna çevrilir.
export const useMicrosoftCalendarApi = () => {
  const [status, setStatus] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    api.get('/v1/integrations/microsoft-calendar')
      .then((res) => {
        if (cancelled) return;
        if (!isOk(res)) {
          setLoadFailed(true);
          setStatus(null);
          return;
        }
        setLoadFailed(false);
        setStatus(res.data?.data || { connected: false });
      })
      .catch(() => {
        if (!cancelled) { setLoadFailed(true); setStatus(null); }
      })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [refreshTrigger]);

  // PUT yanıtı GÜNCEL durumu taşır (backend ToStatusResponse'u döner) — ikinci
  // bir GET atılmaz (Slack'in setRecognitionChannel deseninin AYNISI).
  const connect = useCallback(async (values) => {
    try {
      const res = await api.put('/v1/integrations/microsoft-calendar', {
        azureTenantId: values.azureTenantId,
        clientId: values.clientId,
        clientSecret: values.clientSecret,
      });
      if (!isOk(res)) return false;
      setStatus(res.data?.data || null);
      setLoadFailed(false);
      return true;
    } catch {
      // Yazmalarda interceptor GERÇEKTEN reddeder (okumaların aksine) ve
      // toast'ı kendisi atar — burada yalnız butonun kilidini açmak kalır.
      return false;
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      await api.delete('/v1/integrations/microsoft-calendar');
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // POST test — MicrosoftCalendarTestResult {status, errorClass, error}.
  // İÇ hata (`integrations.msCalendar.testFailed`) KENDİ inline kutumuzda
  // gösterilir; interceptor'ın genel toast'ı skipErrorToast ile bastırılır
  // (WebhooksTab/SlackIntegrationCard'ın AYNI deseni).
  const testConnection = useCallback(async () => {
    try {
      const res = await api.post('/v1/integrations/microsoft-calendar/test', {}, {
        skipErrorToast: ['integrations.msCalendar.testFailed', 'integrations.msCalendar.notConnected'],
      });
      return { ok: true, data: res.data?.data };
    } catch (err) {
      return { ok: false, message: err.response?.data?.message };
    }
  }, []);

  return {
    status, fetching, loadFailed, refresh, connect, disconnect, testConnection,
  };
};

// Hata sınıfı → çeviri anahtarı. Backend üç sınıf döndürür ve ÜÇÜ DE FARKLI
// aksiyon ister; tek bir "bağlantı hatası" metni bu ayrımı yok ederdi
// (bu programda "her hata aynı mesajı veriyordu" kusuru iki kez çıktı).
export const MS_CALENDAR_ERROR_KEYS = {
  auth: 'integrations.msCalendar.errorAuth',
  transient: 'integrations.msCalendar.errorTransient',
  permanent: 'integrations.msCalendar.errorPermanent',
};

export const msCalendarErrorKey = (errorClass) =>
  MS_CALENDAR_ERROR_KEYS[errorClass] || 'integrations.msCalendar.errorUnknown';
